import { FC } from 'react';
import { ReactComponent as LogoSymbol } from 'resources/images/symbol.svg';
import { ReactComponent as FullLogo } from 'resources/images/full-logo.svg';

import { Flex, FlexProps, Icon, IconProps } from '@chakra-ui/react';
import { useAppSelector } from 'app/store';
import { getAuthenticatedSelector } from 'app/store/Auth/selectors';
import { getMaxWidth, useIsMobile } from 'app/utils/hooks';
import { useSelector } from 'react-redux';
import { getSiderbarSettings } from 'app/store/Settings/selectors';
import { useNavigate } from 'react-router-dom';
import { routes } from 'app/routing/routesConfig';

interface HeaderLogoProps {
  options: {
    isSignedOutWeb?: boolean;
    isInPublicRoute?: boolean;
    showFullLogo?: boolean;
    showShortLogo?: boolean;
  };
  iconProps?: IconProps;
  onClick?: () => void;
}

const MobileHeaderLogoSignedIn: FC<HeaderLogoProps> = ({ onClick }) => (
  <Flex px={4} mr="auto">
    <Icon
      as={LogoSymbol}
      role="img"
      color="M.70"
      fill="M.70"
      height={7}
      width={7}
      data-testid="header logo"
      cursor="pointer"
      onClick={onClick}
    />
  </Flex>
);

const MobileHeaderLogoSignedOut: FC<HeaderLogoProps & FlexProps> = ({
  onClick,
  ...rest
}) => (
  <Flex flex={1} px={4} mr="auto" {...rest}>
    <Icon
      as={FullLogo}
      role="img"
      color="M.70"
      height={6}
      width={136}
      data-testid="header logo"
      cursor="pointer"
      onClick={onClick}
    />
  </Flex>
);

const DesktopHeaderLogo: FC<HeaderLogoProps & FlexProps> = ({
  iconProps,
  onClick,
  ...flexProps
}) => (
  <Flex px={1} mr="auto" {...flexProps}>
    <Icon
      as={LogoSymbol}
      role="img"
      color="M.70"
      fill="M.70"
      height={8}
      width={8}
      data-testid="header logo"
      cursor="pointer"
      onClick={onClick}
      {...iconProps}
    />
  </Flex>
);

export const HeaderLogo: FC<HeaderLogoProps> = (headerLogoProps) => {
  const {
    options: { isInPublicRoute, showShortLogo, showFullLogo },
  } = headerLogoProps;

  const isAuthenticated = useAppSelector(getAuthenticatedSelector);
  const navigate = useNavigate();
  const isMobileView = useIsMobile(false);
  const { width } = useSelector(getSiderbarSettings);

  const props = {
    ...headerLogoProps,
    mx: 2,
    flexShrink: 0,
    width,
    maxWidth: getMaxWidth('sidebar'),
    onClick: () => {
      navigate(
        isAuthenticated
          ? isMobileView
            ? routes.settings.path
            : routes.songs.path
          : routes.signIn.path
      );
    },
  };

  if (showShortLogo) {
    return (
      <DesktopHeaderLogo
        {...props}
        iconProps={{ height: 6, width: 6 }}
        pr={4}
      />
    );
  }

  if (isMobileView) {
    if (isInPublicRoute) {
      return <MobileHeaderLogoSignedOut {...props} />;
    }
    if (isAuthenticated) {
      return <MobileHeaderLogoSignedIn {...props} />;
    }
    return <MobileHeaderLogoSignedOut {...props} />;
  }

  if (showFullLogo || !isAuthenticated) {
    return <MobileHeaderLogoSignedOut pl={12} {...props} />;
  }

  return <DesktopHeaderLogo {...props} />;
};
